import React, { useMemo, useRef, useEffect, useState } from "react";
import moment from "moment";
import styles from "./index.module.scss";
import Timer from "../../../stories/common/timer";
import ContactDropdown from "../../../stories/common/flipper-vault-card/contact-dropdown";
import CallingStatusTag from "../../../stories/common/calling-status-tag";
import VaultTag from "../../../stories/common/vault-tag";
import AddNotesDialog from "../add-notes";
import CallRecordsDialog from "../call-records";
import InvestorDetailsSidebar from "../../../stories/common/calling-pipeline-header/investor-details-sidebar";
import { OverlayPanel } from "primereact/overlaypanel";
// import { formatPhoneNumber } from "../../../utils/utils";
interface InvCardProps {
  id?: string;
  expiryAt: string;
  accountId: string;
  checkoutId: string;
  releasedAt: string;
  accountName: string;
  email: string;
  engmtStatus?: string;
  plainPhone: string;
  propertyId: string;
  propertySfid?: string;
  accountSfid?: string;
  address: string;
  userName?: string;
  createdAt: string;
  bidCount?: number;
  callingStatus?: string;
  portalStatus: string;
  contacts?: Array<{
    id: string;
    name: string;
    sfid: string;
    email: string;
    mobile: string;
  }>;
  callRecords?: Array<{
    callOutcome: string;
    callNotes: string;
    followupDate: string;
    cityOfInterest: string;
  }>;
}
export default function CallingPipelineMobileCard({
  address,
  checkoutId,
  accountName,
  expiryAt,
  createdAt,
  releasedAt,
  email,
  plainPhone,
  accountId,
  userName,
  propertyId,
  bidCount,
  contacts,
  accountSfid,
  propertySfid,
  engmtStatus,
  portalStatus,
  callRecords
}: InvCardProps) {
  const [visible, setVisibleNotes] = useState(false);
  const [visibleInvDetail, setVisibleInvDetail] = React.useState(false);
  const [visibleCallRecords, setVisibleCallRecords] = useState(false);
  const [isExpired, setIsExpired] = useState(
    moment.utc(expiryAt).isBefore(moment().utc())
  );
  const [contact, setContact] = useState(contacts?.[0]);
  const [, setShowReleaseBtn] = useState(
    createdAt &&
      -moment.utc(createdAt).diff(moment().utc(), "minutes") <=
        +`${process.env.REACT_APP_RELEASE_TIMER}`
      ? true
      : false
  );

  const vaultStatus = useMemo(() => {
    if (releasedAt !== null) {
      return "RELEASED";
    } else if (isExpired && releasedAt === null) {
      return "EXPIRED";
    } else {
      return "CHECKED OUT";
    }
  }, [releasedAt, isExpired]);

  const cityOfInterestRaw = callRecords?.[0]?.cityOfInterest || "";
  const cities = useMemo(
    () =>
      (cityOfInterestRaw || "")
        .split(",")
        .map((c: string) => c.trim())
        .filter((c: string) => c.length > 0),
    [cityOfInterestRaw]
  );
  const visibleCitiesText = useMemo(
    () => cities.slice(0, 1).join(", "),
    [cities]
  );
  const remainingCities = useMemo(() => cities.slice(1), [cities]);
  const moreOpRef = useRef<OverlayPanel | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const inst: any = moreOpRef?.current as any;
      if (!inst) return;
      try {
        const el =
          typeof inst.getElement === "function"
            ? inst.getElement()
            : (inst as any)?.container || (inst as any)?.overlay || null;
        if (!el) return;
        if (typeof inst.hide === "function") inst.hide();
      } catch (_) {}
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <>
      <AddNotesDialog
        visible={visible}
        setVisible={setVisibleNotes}
        placeholder="Add notes..."
        heading="Add Notes"
        accountId={accountId}
        propertyId={propertyId}
        isEdit={false}
      />
      <CallRecordsDialog
        visible={visibleCallRecords}
        setVisible={setVisibleCallRecords}
        accountId={accountId}
        propertyId={propertyId}
        checkoutId={checkoutId}
      />
      <InvestorDetailsSidebar
        visibleInvDetail={visibleInvDetail}
        setVisibleInvDetail={setVisibleInvDetail}
        setVisibleNotes={setVisibleNotes}
        setVisibleCallRecords={setVisibleCallRecords}
        accountId={accountId}
        propertyId={propertyId}
        position="bottom"
        checkoutId={checkoutId}
      />
      <div
        className={styles.invCardWrap}
        onClick={() => setVisibleInvDetail(true)}
      >
        <div
          className={`${styles.invCardWrapper} ${
            portalStatus === "Pending Price Cut" ? styles.pendingPriceCut : ""
          }`}
        >
          {callRecords !== null && callRecords?.[0]?.followupDate && (
            <div className={styles.scheduledCall}>
              {moment
                .utc(callRecords?.[0]?.followupDate)
                .local()
                .isBefore(moment()) ? (
                <span className={styles.followupDue}>
                  Follow-up Due on{" "}
                  {moment
                    .utc(callRecords?.[0]?.followupDate)
                    .local()
                    .format("MMM Do")}
                </span>
              ) : (
                <span className={styles.followupScheduled}>
                  Follow-up Scheduled on{" "}
                  {moment
                    .utc(callRecords?.[0]?.followupDate)
                    .local()
                    .format("MMM Do")}
                </span>
              )}
            </div>
          )}
          <div className={styles.AccName}>{accountName}</div>

          <div className={styles.topRow}>
            <div className={styles.contactInfo}>
              <ContactDropdown
                flipperData={{
                  contacts: contacts,
                  acctSfid: accountSfid,
                  acctId: accountId,
                  propertyId: propertyId,
                  propertySfid: propertySfid
                }}
                hideInfo={vaultStatus !== "CHECKED OUT"}
                contact={contact}
                setContact={setContact}
              />
            </div>
            <div className={styles.TimerWrapper}>
              {!isExpired && expiryAt && releasedAt === null && (
                <Timer
                  className={styles.Timer}
                  acctId={accountId}
                  time={expiryAt}
                  setShowReleaseBtn={setShowReleaseBtn}
                  onExpire={() => {
                    setIsExpired(true);
                  }}
                  createdAt={createdAt}
                  classNameTitle={styles.TimerTitle}
                  classNameClock={styles.classNameClock}
                />
              )}
            </div>
          </div>
          <div className={styles.invCardDetails}>
            <div className={styles.invCardLeft}>
              <div className={styles.AccAddress}>
                <div className={styles.addressLine1}>
                  {address?.split(",")[0]},
                </div>
                <div className={styles.addressLine2}>
                  {address?.split(",")[1]}, {address?.split(",")[2]}
                </div>
              </div>
              <div className={styles.callStatus}>
                {callRecords?.[0]?.callOutcome ? (
                  <CallingStatusTag
                    callingStatus={callRecords?.[0]?.callOutcome || null}
                  />
                ) : (
                  <VaultTag status={vaultStatus} isLocked={false} />
                )}
              </div>
            </div>
            <div className={styles.invCardRight}>
              <div className={styles.cityName}>
                {cities.length > 0 && (
                  <>
                    <img
                      src="/images/city-of-inst.svg"
                      alt=""
                      width={20}
                      height={20}
                    />
                    {visibleCitiesText}
                    {remainingCities.length > 0 && (
                      <>
                        {" "}
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            moreOpRef.current?.toggle(e);
                          }}
                        >
                          +{remainingCities.length}
                        </span>
                        <OverlayPanel
                          ref={moreOpRef}
                          className={styles.tooltip}
                        >
                          <div>{remainingCities.join(", ")}</div>
                        </OverlayPanel>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className={styles.buttonWrap}>
                <img
                  src="/images/add-notes-black.svg"
                  alt=""
                  className={
                    vaultStatus === "RELEASED" || vaultStatus === "EXPIRED"
                      ? `${styles.disabled}`
                      : ``
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (vaultStatus === "RELEASED" || vaultStatus === "EXPIRED") {
                      return;
                    }
                    setVisibleNotes(true);
                  }}
                />
                <img
                  src="/images/add-call-black.svg"
                  alt=""
                  className={
                    vaultStatus === "RELEASED" || vaultStatus === "EXPIRED"
                      ? `${styles.disabled}`
                      : ``
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    if (vaultStatus === "RELEASED" || vaultStatus === "EXPIRED") {
                      return;
                    }
                    setVisibleCallRecords(true);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
